'use client'

import clsx from 'clsx'
import React, { ReactElement, ReactNode } from 'react'
import ModalDefault from 'react-modal'

import styles from './styles.module.scss'

import { BUTTON_COLOR } from '../../atoms/fields/Button/button.enum'
import IconSvg from '../../atoms/icons/icon-svg'
import Scrollbars from '../../layout/scrollbars/scrollbars'
import Button from '../../../components/atoms/fields/Button/Button'
import { MobileBackPage } from '../../../components/layout/mobile/mobile-back-page'
import { breakpoints, useMediaQuery } from '../../../lib/hooks'
import { cn } from '../../../shadcn/utils'

const customStyles = {
  content: {
    padding: 0,
    margin: 0,
    inset: 0,
    borderRadius: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    height: '100%',
  },
  overlay: {
    background: 'transparent',
    border: 'none',
    zIndex: 100000,
    pointerEvents: 'auto',
  },
  portal: {
    zIndex: 999,
  },
} as const

interface IButtonFooter {
  label: string
  onClick?: (e: React.MouseEvent) => void
  disabled?: boolean
  isLoading?: boolean
  fullWidth?: boolean
  color?: BUTTON_COLOR
  id?: string
  className?: string
}

export interface IModalFooter {
  subButton?: IButtonFooter
  button?: IButtonFooter
  subContent?: ReactNode
}

export interface IModalProps {
  id?: string
  isOpen: boolean
  closeModal?: (evt: React.MouseEvent) => void
  children: React.ReactNode
  mobileHeader?: ReactElement
  customHeader?: ReactNode
  customFooter?: ReactNode
  renderButtons?: () => ReactNode
  title?: string | ReactElement
  styleHeader?: React.CSSProperties
  styleChildren?: React.CSSProperties
  scrollbarStyle?: React.CSSProperties
  styleTitle?: React.CSSProperties
  style?: React.CSSProperties
  isMobileOnly?: boolean
  onScroll?: (evt: React.UIEvent<HTMLDivElement, UIEvent>) => void
  footerStyle?: React.CSSProperties
  footer?: IModalFooter
  navBar?: ReactElement
  noScroll?: boolean
  className?: string
  forceDesktopRender?: boolean
  applyInnerStyle?: boolean
  dataTestId?: string
  childrenClassName?: string
}

const Modal = ({
  id,
  isOpen,
  closeModal,
  children,
  mobileHeader,
  customHeader,
  customFooter,
  renderButtons,
  title,
  styleHeader = {},
  styleChildren = {},
  style = {},
  scrollbarStyle = {},
  onScroll,
  footerStyle,
  footer,
  styleTitle = {},
  navBar,
  noScroll,
  className,
  forceDesktopRender,
  applyInnerStyle = true,
  dataTestId = 'modal',
  childrenClassName,
}: IModalProps) => {
  const isMobile = useMediaQuery(breakpoints.newMobile)

  if (
    !forceDesktopRender &&
    isMobile &&
    title !== 'Découvrez immédiatement nos suggestions de séminaires'
  ) {
    if (!isOpen) {
      return null
    }
    return (
      <MobileBackPage
        backPageProps={{
          title,
          styleTitle,
          onGoBack: closeModal && ((e) => closeModal && closeModal(e)),
        }}
        isModal={true}
        navBar={navBar}
      >
        {mobileHeader}
        <div
          className={clsx(styles.children, styles.childrenMobile, className)}
          style={styleChildren}
          onScroll={onScroll ? (evt) => onScroll(evt) : undefined}
        >
          {children}
        </div>
        {renderButtons && (
          <div className={styles.bannerButtons} style={footerStyle}>
            {renderButtons()}
          </div>
        )}
        {footer && (
          <div className={styles.footer}>
            {footer.subButton ? (
              <div
                className={styles.underline}
                onClick={footer.subButton.onClick}
                id={footer.subButton.id}
              >
                {footer.subButton.label}
              </div>
            ) : (
              <div>{footer.subContent || ''}</div>
            )}
            {footer.button ? (
              <Button
                onClick={footer.button.onClick}
                disabled={footer.button.disabled}
                loading={footer.button.isLoading}
                fullWidth={footer.button.fullWidth}
                className={footer.button.className}
                id={footer.button.id}
              >
                {footer.button.label}
              </Button>
            ) : (
              <div></div>
            )}
          </div>
        )}
      </MobileBackPage>
    )
  }
  return (
    <ModalDefault
      id={id}
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={customStyles}
      ariaHideApp={false}
      className={clsx({ [styles.noScroll]: noScroll })}
    >
      <div
        className={styles.backgroundModal}
        onClick={(e) => closeModal && closeModal(e)}
      />

      <div
        className={`${styles.contentModal} ${className}`}
        style={style}
        data-testid={dataTestId}
      >
        <>
          {customHeader ? (
            customHeader
          ) : typeof title === 'string' ? (
            <div className={styles.header} style={styleHeader}>
              <div className={styles.title} style={styleTitle}>
                {title}
              </div>
              {closeModal && (
                <IconSvg.Close
                  color='black'
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => closeModal && closeModal(e)}
                />
              )}
            </div>
          ) : (
            title
          )}

          <Scrollbars
            innerStyle={
              applyInnerStyle
                ? { padding: '20px 30px', ...scrollbarStyle }
                : undefined
            }
            className={cn(`${styles.children}`, childrenClassName)}
            style={styleChildren}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            onScroll={onScroll ? (evt) => onScroll(evt) : undefined}
            autoHeight={true}
            noScroll={noScroll}
          >
            {children}
          </Scrollbars>

          {customFooter ? (
            customFooter
          ) : (
            <>
              {renderButtons && (
                <div className={styles.bannerButtons} style={footerStyle}>
                  {renderButtons()}
                </div>
              )}

              {footer && (
                <div className={styles.footer} style={footerStyle}>
                  {footer.subButton ? (
                    <div
                      className={styles.underline}
                      onClick={footer.subButton.onClick}
                    >
                      {footer.subButton.label}
                    </div>
                  ) : (
                    <div></div>
                  )}
                  {footer.button ? (
                    <Button
                      onClick={footer.button.onClick}
                      disabled={footer.button.disabled}
                      loading={footer.button.isLoading}
                      fullWidth={footer.button.fullWidth}
                      className={footer.button.className}
                      color={footer.button.color}
                    >
                      {footer.button.label}
                    </Button>
                  ) : (
                    <div />
                  )}
                </div>
              )}
            </>
          )}
        </>
      </div>
    </ModalDefault>
  )
}

export default Modal
